import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription, switchMap, tap } from 'rxjs';
import { AuthService } from '../../core/services/auth.service';
import { SpotifyService } from '../../core/services/spotify.service';
import { UserService } from '../../core/services/user.service';
import { YoutubeService } from '../../core/services/youtube.service';

type Provider = 'spotify' | 'youtube';

@Component({
  selector: 'app-connect-callback',
  templateUrl: './connect-callback.page.html',
  styleUrls: ['./connect-callback.page.scss']
})
export class ConnectCallbackPage implements OnInit, OnDestroy {
  provider?: Provider;
  code?: string | null;
  loading = true;
  success = false;
  error?: string | null;
  private subscriptions: Subscription[] = [];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private spotifyService: SpotifyService,
    private youtubeService: YoutubeService,
    private userService: UserService,
    private authService: AuthService
  ) {}

  ngOnInit(): void {
    this.provider = (this.route.snapshot.queryParamMap.get('provider') as Provider) || undefined;
    this.code = this.route.snapshot.queryParamMap.get('code');

    if (!this.provider || !this.code) {
      this.handleError('Missing provider or code in the callback URL.');
      return;
    }

    if (!this.authService.token) {
      this.handleError('Please sign in to link your account.', '/auth/login');
      return;
    }

    const sub = this.handleLink(this.provider, this.code).subscribe({
      next: () => {
        this.success = true;
        this.loading = false;
        setTimeout(() => this.router.navigateByUrl('/app/account'), 1200);
      },
      error: err => {
        const message =
          err?.error?.message ||
          (err?.status === 401 ? 'Session expired. Please sign in again.' : 'We could not link your account.');
        this.handleError(message, err?.status === 401 ? '/auth/login' : undefined);
      }
    });
    this.subscriptions.push(sub);
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(s => s.unsubscribe());
  }

  private handleLink(provider: Provider, code: string) {
    const handler =
      provider === 'spotify'
        ? this.spotifyService.handleCallback(code)
        : this.youtubeService.handleCallback(code);

    return handler.pipe(
      switchMap(() => this.userService.loadMe()),
      tap(user => this.userService.setUser(user))
    );
  }

  goToAccount(): void {
    this.router.navigateByUrl('/app/account');
  }

  private handleError(message: string, redirect?: string): void {
    this.error = message;
    this.loading = false;
    if (redirect) {
      setTimeout(() => this.router.navigateByUrl(redirect), 1200);
    }
  }
}
